import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PatientReportDocumentComponent } from '@modules/patient/patient-report-document/patient-report-document.component';
import { PatientReportDocumentService } from '@modules/patient/patient-report-document/patient-report-document.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { DialogService } from '@services/dialog.service';
import { PatientHttpService } from '@services/http/patient.http.service';
import { PatientReportExportMode } from '@shared/event-tracker/models/patient-report-export-mode.enum';
import { selectIsEhrEnabled } from '@shared/store/clients/clients.selectors';
import { downloadBlob } from '@shared/utils/utils';
import { TuiButton, TuiDialogContext, TuiNotificationService, TuiTitle } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { catchError, combineLatest, EMPTY, filter, first, map, Observable, Subscription, switchMap, tap } from 'rxjs';
import { PatientEncounterPickerComponent } from '../patient-encounter-picker/patient-encounter-picker.component';

@Component({
  selector: 'app-patient-report',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    TranslocoDirective,
    TuiButton,
    TuiTitle,
    PatientReportDocumentComponent,
  ],
  templateUrl: './patient-report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col justify-between gap-4 overflow-auto p-6' },
  providers: [PatientReportDocumentService],
})
@UntilDestroy()
export class PatientReportComponent {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly patientService = inject(PatientHttpService);
  private readonly translocoService = inject(TranslocoService);
  private readonly alertService = inject(TuiNotificationService);

  protected readonly patientReportDocumentService = inject(PatientReportDocumentService);

  private readonly context = inject<
    TuiDialogContext<
      void,
      {
        clientCode: string;
        patient: Patient;
        title: string;
        departmentIds: number[];
      }
    >
  >(POLYMORPHEUS_CONTEXT);

  @Input() clientCode: string = this.context.data?.clientCode;
  @Input() patient: Patient = this.context.data?.patient;
  @Input() title: string = this.context.data?.title;
  @Input() departmentIds: number[] = this.context.data?.departmentIds || [];

  readonly isExportEhrEnabled$ = this.store
    .select(selectIsEhrEnabled(this.clientCode))
    .pipe(map((isEhrEnabled) => isEhrEnabled && this.patient.isGamLinked));

  close(): void {
    this.context.completeWith();
  }

  download(content: Blob): void {
    downloadBlob(content, `${this.title}.pdf`);
    this.patientReportDocumentService.trackPatientReportExportEvent(PatientReportExportMode.DOWNLOAD);
    this.isExportEhrEnabled$.pipe(first()).subscribe((isEnabled) => {
      if (!isEnabled) {
        this.close();
      }
    });
  }

  export(): void {
    combineLatest([this.openEncounterPicker(), this.patientReportDocumentService.patientReportRequest$])
      .pipe(
        filter(([encounterId]) => !!encounterId),
        switchMap(([encounterId, patientReportRequest]) => {
          const loadingSubscription = this.getExportLoadingToast();
          return this.patientService.export(this.clientCode, this.patient.id, encounterId, patientReportRequest).pipe(
            tap(() => {
              this.handleExportSuccess(loadingSubscription);
            }),
            catchError((error) => {
              this.handleExportError(error, loadingSubscription);
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe();
    this.close();
  }

  private openEncounterPicker(): Observable<string> {
    return this.dialogService.openComponent<PatientEncounterPickerComponent, string>(PatientEncounterPickerComponent, {
      label: this.translocoService.translate('Exporter un rapport patient dans le DPI'),
      data: {
        clientCode: this.clientCode,
        patientId: this.patient.id,
      },
    });
  }

  private getExportLoadingToast(): Subscription {
    return this.alertService
      .open(this.translocoService.translate("L'export sera prêt dans quelques instants"), {
        label: this.translocoService.translate('Export en cours'),
        appearance: 'info',
        autoClose: 10000,
        closable: false,
      })
      .subscribe();
  }

  private handleExportError(error, loadingSubscription: Subscription): void {
    console.error(error);
    loadingSubscription.unsubscribe();
    this.alertService
      .open(this.translocoService.translate("Une erreur est survenue lors de l'export du rapport dans le DPI"), {
        label: this.translocoService.translate("Impossible d'exporter le rapport"),
        appearance: 'negative',
      })
      .subscribe();
  }

  private handleExportSuccess(loadingSubscription: Subscription): void {
    loadingSubscription.unsubscribe();
    this.patientReportDocumentService.trackPatientReportExportEvent(PatientReportExportMode.EHR);
    this.alertService
      .open(this.translocoService.translate('Le rapport du patient a été exporté dans le DPI'), {
        label: this.translocoService.translate('Rapport exporté'),
        appearance: 'positive',
      })
      .subscribe();
  }
}
